import React, { useState, useEffect, useContext } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, 
    Button, TextField, IconButton, Alert, Typography, Box, Chip, Fade, Tabs, Tab
} from '@mui/material';
import { CopyToClipboard } from "react-copy-to-clipboard";
import ApiIcon from '@mui/icons-material/Api';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { createAPIKey } from "../API/KB/KBAPI.js";
import { AlertContext } from "../App/App";
import {convertTextWithUrlsToStructuredPrompt, getQueryParamValue, isMobile} from "../App/utils";
import { Link } from 'react-router-dom';

export const APIKeyWidget = ({ kbId, kbTitle, setBlockingLoading }) => {
    const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);
    const [showWidget, setShowWidget] = useState(false);
    const [apiKeyData, setApiKeyData] = useState(null); // {apiKey, prompt}
    const [selectedTab, setSelectedTab] = useState(0); // 0 = Basic, 1 = Conversation
    const [shouldPulse, setShouldPulse] = useState(false);
    const { setSystemAlert } = useContext(AlertContext);

    useEffect(() => {
        // Check sessionStorage first for existing API key data
        const existingApiKeyData = sessionStorage.getItem('generatedAPIKeyData');
        if (existingApiKeyData) {
            try {
                const parsed = JSON.parse(existingApiKeyData);
                setApiKeyData(parsed);
                setShowWidget(true);
                // Don't pulse for existing data - user has seen it before
                return;
            } catch (e) {
                console.error('Failed to parse stored API key data:', e);
                sessionStorage.removeItem('generatedAPIKeyData');
            }
        }

        // Generate new API key if parameters are present
        const generateAPIParam = getQueryParamValue('generateAPI');
        const promptParam = getQueryParamValue('prompt') || 'Your prompt here';
        
        if (generateAPIParam === 'true' && promptParam && kbId && kbTitle) {
            const generateAutoAPIKey = async () => {
                try {
                    setBlockingLoading({ text: 'Generating API key...', icon: '🔑' });
                    const { apiKey } = await createAPIKey(`${kbTitle} Auto-Generated`);
                    
                    // Store in sessionStorage instead of showing dialog immediately
                    const apiKeyData = {
                        apiKey: apiKey,
                        prompt: convertTextWithUrlsToStructuredPrompt(promptParam, true),
                        kbId: kbId,
                        kbTitle: kbTitle,
                        timestamp: Date.now()
                    };
                    
                    sessionStorage.setItem('generatedAPIKeyData', JSON.stringify(apiKeyData));
                    setApiKeyData(apiKeyData);
                    setShowWidget(true);
                    
                    // Check if this is first time showing - pulse only on first time
                    const hasShownBefore = sessionStorage.getItem('apiWidgetShownBefore');
                    if (!hasShownBefore) {
                        setShouldPulse(true);
                        sessionStorage.setItem('apiWidgetShownBefore', 'true');
                        // Stop pulsing after 3 pulses
                        setTimeout(() => setShouldPulse(false), 3000);
                    }
                    
                    // Clear the generateAPI parameter from URL
                    const url = new URL(window.location);
                    url.searchParams.delete('generateAPI');
                    window.history.replaceState({}, '', url);
                } catch (error) {
                    setSystemAlert({ 
                        msg: `Failed to generate API key: ${error.message}`, 
                        type: 'error' 
                    });
                } finally {
                    setTimeout(() => setBlockingLoading(false), 500);
                }
            };
            
            // Generate API key after a short delay to ensure everything is loaded
            const timer = setTimeout(generateAutoAPIKey, 1500);
            return () => clearTimeout(timer);
        }
    }, [kbId, kbTitle, setBlockingLoading, setSystemAlert]);

    const handleCloseDialog = () => {
        // Just close the dialog, don't clear anything
        setApiKeyDialogOpen(false);
    };

    const handleGotIt = () => {
        // Only clear when user explicitly clicks "Got it"
        setApiKeyDialogOpen(false);
        setShowWidget(false);
        sessionStorage.removeItem('generatedAPIKeyData');
    };

    const handleOpenDialog = () => {
        setApiKeyDialogOpen(true);
    };

    const handleCloseWidget = () => {
        setShowWidget(false);
        // Clear from sessionStorage when user dismisses
        sessionStorage.removeItem('generatedAPIKeyData');
    };

    const copyToClipboard = (text, successMessage) => {
        setSystemAlert({ 
            msg: successMessage, 
            type: 'success', 
            duration: 2000 
        });
    };

    const getBasicRequest = () => {
        if (!apiKeyData) return '';
        const messageValue = Array.isArray(apiKeyData.prompt) 
            ? JSON.stringify(apiKeyData.prompt)
            : `"${apiKeyData.prompt.replace(/"/g, '\\"').replace(/\n/g, ' ').replace(/\r/g, ' ')}"`;
        return `curl -X POST https://${apiKeyData.kbId}.apps.openkbs.com/api \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKeyData.apiKey}" \\
  -d '{"message": ${messageValue}}'`;
    };

    const getConversationRequest = () => {
        if (!apiKeyData) return '';
        const messageValue = Array.isArray(apiKeyData.prompt) 
            ? JSON.stringify(apiKeyData.prompt)
            : `"${apiKeyData.prompt.replace(/"/g, '\\"').replace(/\n/g, ' ').replace(/\r/g, ' ')}"`;
        return `# Start conversation
curl -X POST https://${apiKeyData.kbId}.apps.openkbs.com/api \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKeyData.apiKey}" \\
  -d '{"message": ${messageValue}}'

# Continue with history  
curl -X POST https://${apiKeyData.kbId}.apps.openkbs.com/api \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKeyData.apiKey}" \\
  -d '{
    "chatId": "CHAT_ID_FROM_RESPONSE",
    "message": "Follow-up question here",
    "includeHistory": true,
    "historyLimit": 10
  }'`;
    };

    const getCurrentRequest = () => {
        return selectedTab === 0 ? getBasicRequest() : getConversationRequest();
    };

    const getCurrentRequestLabel = () => {
        return selectedTab === 0 ? 'Basic Request' : 'Conversation Flow';
    };

    if (!apiKeyData) {
        return null; // Don't render anything if no API key data
    }

    return (
        <>
            {/* Subtle Top Widget - Only shows when API key is generated */}
            {showWidget && (
                <Fade in={showWidget}>
                    <Box
                        sx={{
                            position: 'fixed',
                            top: isMobile ? '68px' : '140px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 1200,
                            display: 'flex',
                            gap: 1,
                            alignItems: 'center'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            <Box
                                onClick={handleOpenDialog}
                                sx={{
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    backgroundColor: '#3B86C9',
                                    color: 'white',
                                    padding: isMobile ? '8px 16px' : '12px 20px',
                                    borderRadius: '24px',
                                    fontSize: isMobile ? '14px' : '16px',
                                    fontWeight: '600',
                                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                                    '&:hover': {
                                        transform: 'scale(1.05)',
                                        boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                                        animation: 'none'
                                    },
                                    transition: 'all 0.3s ease',
                                    minWidth: isMobile ? '200px' : '240px',
                                    justifyContent: 'space-between',
                                    '@keyframes attentionPulse': {
                                        '0%': { 
                                            transform: 'scale(1)',
                                            boxShadow: '0 4px 12px rgba(59, 134, 201, 0.3)'
                                        },
                                        '50%': { 
                                            transform: 'scale(1.03)',
                                            boxShadow: '0 6px 16px rgba(59, 134, 201, 0.5)'
                                        },
                                        '100%': { 
                                            transform: 'scale(1)',
                                            boxShadow: '0 4px 12px rgba(59, 134, 201, 0.3)'
                                        }
                                    },
                                    animation: shouldPulse ? 'attentionPulse 1s ease-in-out infinite' : 'none'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ApiIcon sx={{ 
                                        fontSize: isMobile ? '18px' : '20px', 
                                        color: 'white',
                                        '@keyframes iconGlow': {
                                            '0%': { 
                                                filter: 'drop-shadow(0 0 2px rgba(255, 255, 255, 0.4))'
                                            },
                                            '50%': { 
                                                filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.8))'
                                            },
                                            '100%': { 
                                                filter: 'drop-shadow(0 0 2px rgba(255, 255, 255, 0.4))'
                                            }
                                        },
                                        animation: shouldPulse ? 'iconGlow 1s ease-in-out infinite' : 'none'
                                    }} />
                                    <Box>
                                        <Box sx={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '600' }}>
                                            API Key Created
                                        </Box>
                                        <Box sx={{ fontSize: isMobile ? '10px' : '12px', opacity: 0.9 }}>
                                            Click here to access API endpoint
                                        </Box>
                                    </Box>
                                </Box>
                                <KeyboardArrowRightIcon sx={{ 
                                    fontSize: isMobile ? '16px' : '18px',
                                    color: 'white',
                                    '@keyframes pulse': {
                                        '0%': { opacity: 0.6 },
                                        '50%': { opacity: 1 },
                                        '100%': { opacity: 0.6 }
                                    },
                                    animation: 'pulse 1.5s infinite'
                                }} />
                            </Box>
                        </Box>
                    </Box>
                </Fade>
            )}

            {/* API Integration Dialog - Consistent with App Style */}
            <Dialog open={apiKeyDialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
                <DialogTitle>API Integration Ready</DialogTitle>
                <DialogContent>

                    {/* Request Method Tabs */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                        <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
                            <Tab label="Basic" style={{ textTransform: 'none' }} />
                            <Tab label="Conversation" style={{ textTransform: 'none' }} />
                        </Tabs>
                    </Box>

                    {/* Request Content */}
                    <TextField
                        fullWidth
                        multiline
                        variant="outlined"
                        label={getCurrentRequestLabel()}
                        value={getCurrentRequest()}
                        InputProps={{ 
                            readOnly: true,
                            style: {
                                fontFamily: 'monospace',
                                fontSize: '13px',
                                lineHeight: '1.4'
                            }
                        }}
                        sx={{ mb: 2 }}
                    />

                    <Alert severity="info" sx={{ mt: 2 }}>
                        Keep your API key secure and rotate regularly. <br /><Link to="?tab=access&subtab=api_keys" style={{ color: '#1976d2', textDecoration: 'none' }}>Manage all keys in Access Control — includes full API documentation</Link>.
                    </Alert>
                </DialogContent>
                
                <DialogActions>
                    <CopyToClipboard text={getCurrentRequest()}>
                        <Button 
                            variant="contained" 
                            onClick={() => copyToClipboard(getCurrentRequest(), `${getCurrentRequestLabel()} copied to clipboard!`)}
                            sx={{ mr: 1 }}
                        >
                            Copy
                        </Button>
                    </CopyToClipboard>
                    <Button variant="outlined" onClick={handleGotIt} color="primary">
                        Got it
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};